// ABMGuide.js — Coursera-branded, print-ready ABM Marketing & Sales 101
// Page 1 = refreshed cover (uses public thumbnail). Pages 2–9 = your slides.
// Pages 10–12 = interactive (Roundtable, Syndication, Wheel).

import React, { useEffect } from "react";
import RoundtableDashboard from "./RoundtableDashboard";
import ContentSyndicationDashboard from "./ContentSyndicationDashboard";
import WheelBuilderCoursera from "./WheelBuilderCoursera";

/* =========================
   Assets
========================= */
const LOGO_SVG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.svg";
const LOGO_PNG =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/Coursera%20-%20Blue.png";
const C_RGB =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/logos/conduit/RGB_Conduit.svg";
const HERO_THUMB =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/abm_101.png";
const COVER_THUMB =
  "https://pub-065b7688176a4969bec21707aed69fdc.r2.dev/coursera/thumbnails/abm_101.png";

/* =========================
   Title helper
========================= */
function useAutoTitle(title = "ABM Marketing & Sales 101") {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    return () => {
      document.title = prev;
    };
  }, [title]);
}

/* =========================
   Styles (includes contrast tweaks we landed on)
========================= */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@400;600;700&display=swap');

:root{
  --c-blue:#2A73CC; --c-navy:#0E2B57; --ink:#0B1A3C; --ink70:#0B1A3CB3;
  --muted:#44546A; --line:#E6EDF2; --bg:#F4F6FA; --card:#FFFFFF;
  --r-lg:16px; --r-md:12px; --r-sm:8px;
  --sh-sm:0 2px 4px rgba(0,0,0,.08); --sh-md:0 6px 20px rgba(0,0,0,.12);
}

*{box-sizing:border-box}
body{margin:0; background:var(--bg); color:var(--ink);
  font-family:"Source Sans Pro", system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;}

.pdf-top{
  position:sticky; top:0; z-index:40; display:flex; align-items:center; justify-content:space-between;
  padding:12px 18px; background:#fff; border-bottom:1px solid var(--line);
}
.brand{display:flex; align-items:center; gap:8px; color:var(--ink); font-weight:700; text-decoration:none}
.forbiz{color:var(--ink70); font-weight:600}
.top-actions{display:flex; gap:8px}
.btn{display:inline-flex; align-items:center; justify-content:center; gap:8px; padding:10px 14px; border-radius:8px; font-weight:700; border:1px solid transparent; cursor:pointer}
.btn--primary{background:var(--c-blue); color:#fff}
.btn--primary:hover{filter:brightness(.97)}
.btn--ghost{background:transparent; color:var(--ink); border:1px solid var(--line)}

.sheet{width:min(1056px, 92vw); margin:18px auto}
.page{position:relative; background:#fff; border-radius:16px; box-shadow:var(--sh-md); padding:24px; margin:16px 0}
.page__foot{position:absolute; bottom:16px; right:24px; color:var(--muted); font-size:12px}

/* Cover */
/* Cover layout updates */
.intro__hero{
  position:relative;
  color:#fff;
  border-radius:16px;
  padding:28px;
  background:
    radial-gradient(1200px 480px at 115% -20%, rgba(255,255,255,.18), rgba(255,255,255,0) 60%),
    linear-gradient(180deg, var(--c-blue) 0%, #1f62b2 100%);
  /* NEW: 2-col grid */
  display:grid;
  grid-template-columns: minmax(0,1.2fr) minmax(280px, 1fr);
  gap:24px;
  align-items:center;
  min-height: clamp(360px, 42vw, 440px);
}
.intro__copy{min-width:0}               /* prevents overflow */
.intro__art{
  margin:0;
  background:#fff;
  border:1px solid var(--line);
  border-radius:14px;
  box-shadow:var(--sh-md);
  padding:10px;
  display:flex;
  align-items:center;
  justify-content:center;
}
.intro__art img{
  width:100%;
  height:auto;
  object-fit:contain;
  border-radius:10px;
}
/* keep the title sizing you like */
.intro__title{margin:0 0 6px; font-size:40px; line-height:48px; font-weight:700}
.intro__subtitle{margin:0 0 12px; font-size:18px; line-height:26px; color:rgba(255,255,255,.95)}

/* Decorative shapes (unchanged) */
.c{position:absolute; opacity:.18; filter:drop-shadow(0 2px 4px rgba(0,0,0,.1))}
.c--left{left:-110px; top:-70px; width:320px}
.c--ring{right:-90px; top:-50px; width:260px; opacity:.24}
.c--chip{right:90px; top:90px; width:100px; opacity:.9; filter:drop-shadow(0 8px 18px rgba(0,0,0,.22))}

/* Responsive: stack on narrow pages so the image stops cramping the copy */
@media (max-width: 980px){
  .intro__hero{ grid-template-columns: 1fr; }
  .intro__art{ order: -1; }          /* image on top on small screens (optional) */
}

.c{position:absolute; opacity:.18; filter:drop-shadow(0 2px 4px rgba(0,0,0,.1))}
.c--left{left:-110px; top:-70px; width:320px}
.c--ring{right:-90px; top:-50px; width:260px; opacity:.24}
.c--chip{right:90px; top:90px; width:100px; opacity:.9; filter:drop-shadow(0 8px 18px rgba(0,0,0,.22))}
.intro__title{margin:0 0 6px; font-size:40px; line-height:48px; font-weight:700}
.intro__subtitle{margin:0 0 12px; font-size:18px; line-height:26px; color:rgba(255,255,255,.95)}
.lede{max-width:780px; background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.18);
  color:#EAF2FF; padding:10px 12px; border-radius:12px}

/* Shared content helpers */
.hero{position:relative; border-radius:16px; padding:24px 24px 28px; overflow:hidden; color:#fff; margin-bottom:16px;
  background:linear-gradient(180deg, var(--c-navy) 0%, #0b2a55 100%)}
.hero__bg{position:absolute; inset:0; opacity:.22}
.hero__wm{position:absolute; left:-110px; top:-50px; width:360px}
.hero__title{position:relative; z-index:1; margin:0; font-size:28px; line-height:34px; font-weight:700}
.hero__sub{position:relative; z-index:1; margin:6px 0 0; opacity:.92}

.grid{display:grid; grid-template-columns:1fr 1fr; gap:16px}
.grid--3{grid-template-columns:repeat(3,minmax(0,1fr))}
@media (max-width:900px){ .grid,.grid--3{grid-template-columns:1fr} }

.panel{background:#fff; border:1px solid var(--line); border-radius:12px; padding:16px}
.panel h3{margin:0 0 8px; font-size:20px; line-height:26px}
.h4{margin:0 0 6px; font-size:16px}
.tight{margin:0; padding-left:18px}
.tight li{margin:6px 0}
.flow{margin:0; padding-left:18px}
.flow li{margin:8px 0}
.note{margin-top:12px; padding:10px 12px; border:1px dashed var(--line); border-radius:12px; background:#FBFCFF}

/* Glossary layout */
.gloss{display:grid; grid-template-columns:220px 1fr; gap:8px 16px}
.gloss__row{display:contents}
.gloss dt{font-weight:700}
.gloss dd{margin:0}

/* Contrast/readability tweaks (the “font colors we worked on”) */
.muted{ color:#0B1A3C !important; }            /* full ink inside panels */
.panel .muted{ font-weight:500; font-size:15.5px; line-height:1.5; }
.tag{display:inline-block;background:#EAF2FF;border:1px solid #D6E6FF;color:#0E2B57;font-weight:700;
  border-radius:999px;padding:2px 8px;font-size:11px}

@media print{
  .pdf-top{display:none !important}
  .sheet{width:auto; margin:0}
  .page{box-shadow:none; border-radius:0; margin:0; padding:20mm; page-break-after:always}
  .page__foot{bottom:10mm; right:20mm}
  .muted,.panel .muted{ color:#000 !important; }
}
.stats{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:12px;margin:8px 0 16px}
.stat{position:relative;background:#fff;border:1px solid var(--line);border-radius:12px;padding:12px 12px 10px;box-shadow:var(--sh-sm)}
.stat::before{content:"";position:absolute;inset:0;border-radius:12px;
  background:radial-gradient(120% 120% at -10% -20%, rgba(42,115,204,.08), rgba(42,115,204,0) 60%)}
.stat__label{font-size:12px;font-weight:700;color:var(--ink70);letter-spacing:.2px;text-transform:uppercase}
.stat__num{display:flex;align-items:baseline;gap:6px;margin-top:4px}
.stat__value{font-size:28px;line-height:30px;font-weight:800;color:var(--ink)}
.stat__unit{font-size:12px;color:var(--muted)}
.stat__hint{margin-top:4px;font-size:12px;color:var(--muted)}
@media (max-width:1100px){ .stats{grid-template-columns:repeat(3,minmax(0,1fr))} }
@media (max-width:760px){ .stats{grid-template-columns:repeat(2,minmax(0,1fr))} }

/* little pill chips (FS / SaaS/Tech) */
.tag{display:inline-block;background:#EAF2FF;border:1px solid #D6E6FF;color:#0E2B57;
  font-weight:700;border-radius:999px;padding:2px 8px;font-size:11px}
  /* Emphasized right-side ABM panel */
  .panel--abmRight{
    position: relative;
    background:#F7FAFF;
    border:1px solid #D9E7FF;
    box-shadow: var(--sh-sm);
  }
  .panel--abmRight::after{
    /* subtle blue bar on the far right edge */
    content:"";
    position:absolute; top:0; right:0; bottom:0; width:6px;
    background: color-mix(in srgb, var(--c-blue) 70%, white);
    border-radius: 0 var(--r-md) var(--r-md) 0;
    opacity:.9;
  }
  .panel__label{
    display:inline-block;
    background:#EEF3FF;
    color:#0B1A3C;
    font-weight:700;
    border:1px solid #D6E6FF;
    border-radius:999px;
    padding:3px 10px;
    font-size:11.5px;
    letter-spacing:.2px;
  }
  
  /* soft grey/blue callout inside the ABM card */
  .callout{
    margin-top:12px; padding:10px 12px; border-radius:12px;
    border:1px solid var(--line);
  }
  .callout--soft{
    background:#FFFFFF; border-color:#E6EDF8;
  }
  
  /* small helper for the “one plan” line */
  .miniHint{
    margin-top:10px; font-size:13px; color:var(--muted);
    background:#F9FBFF; border:1px dashed #D9E7FF;
    padding:8px 10px; border-radius:10px;
  }
  .c--chip { display: none; }
  /* all background accents behind */
  .intro__hero .c { position:absolute; z-index:0; }
  
  /* the wheel/thumbnail above */
  .intro__thumb { position:relative; z-index:2; }
  /* If you use a wrapper around the right-side image/card, give it this class */
  .c--chip { right: -120px; top: 40px; opacity: .25; z-index: 0; }
  
  /* two even columns; collapses to single on small screens */
.grid--split { grid-template-columns: 1fr 1fr; gap: 16px; }
@media (max-width: 900px) { .grid--split { grid-template-columns: 1fr; } }

/* subtle, readable “ABM highlight” panel on the right */
.panel--abmRight{
  background:#F7FAFF;             /* light slate/blue */
  border:1px solid #D6E6FF;
  border-radius:12px;
  box-shadow: var(--sh-sm);
  overflow: visible;               /* avoid clipping badges/shadows */
}

/* small soft note used on the left */
.note--soft{
  background:#FBFCFF;
  border-color:#E6EDF8;
  color:#374151;
}

/* pill label top-left of the ABM panel */
.badge-pill{
  display:inline-block;
  padding:4px 10px;
  border-radius:999px;
  background:#E7F0FF;
  border:1px solid #D6E6FF;
  color:#0E2B57;
  font-size:12px;
  font-weight:700;
}

/* callouts inside the ABM panel */
.callout{
  margin-top:12px;
  padding:12px 14px;
  border-radius:10px;
  background:#FFFFFF;
  border:1px solid #E6EDF8;
  box-shadow: 0 1px 0 rgba(42,115,204,.06) inset;
}
.callout--muted{
  background:#F9FBFF;
  color:#0B1A3C;
}

/* ensure panel borders/shadows aren’t clipped anywhere */
.panel{ overflow: visible; }

.panel--abmRight { background: #F6F9FF; }


`;
/* ================== SCORECARD STRIP ================== */
function StatStrip({
  data = {
    coverage: { value: 72, unit: "%", hint: "ABM accts w/ ≥2 personas" },
    activity: { value: 41, unit: "%", hint: "engaged in last 30–60d" },
    depth: { value: 28, unit: "", hint: "accts w/ 2+ personas" },
    pipeline: { value: 6.2, unit: "M", hint: "qualified ABM pipeline" },
    outcomes: {
      value: 1.1,
      unit: "M",
      hint: "closed-won • early win-rate read",
    },
    clusters: ["FS", "SaaS/Tech"],
  },
}) {
  return (
    <div className="stats" role="group" aria-label="ABM scorecard">
      {/* Coverage */}
      <div className="stat">
        <div className="stat__label">Coverage</div>
        <div className="stat__num">
          <div className="stat__value">
            {data.coverage.value}
            <span className="stat__unit">{data.coverage.unit}</span>
          </div>
        </div>
        <div className="stat__hint">{data.coverage.hint}</div>
        <div className="stat__hint">
          ABM 1:1 + 1:Few{" "}
          {data.clusters?.length ? (
            <>
              {data.clusters.map((k) => (
                <span key={k} className="tag" style={{ marginLeft: 6 }}>
                  {k}
                </span>
              ))}
            </>
          ) : null}
        </div>
      </div>

      {/* Activity */}
      <div className="stat">
        <div className="stat__label">Activity</div>
        <div className="stat__num">
          <div className="stat__value">
            {data.activity.value}
            <span className="stat__unit">{data.activity.unit}</span>
          </div>
        </div>
        <div className="stat__hint">{data.activity.hint}</div>
      </div>

      {/* Depth */}
      <div className="stat">
        <div className="stat__label">Depth</div>
        <div className="stat__num">
          <div className="stat__value">{data.depth.value}</div>
        </div>
        <div className="stat__hint">{data.depth.hint}</div>
        <div className="stat__hint">Multi-threaded buying groups</div>
      </div>

      {/* Pipeline */}
      <div className="stat">
        <div className="stat__label">Pipeline</div>
        <div className="stat__num">
          <div className="stat__value">
            ${data.pipeline.value}
            <span className="stat__unit">{data.pipeline.unit}</span>
          </div>
        </div>
        <div className="stat__hint">{data.pipeline.hint}</div>
        <div className="stat__hint">ABM 1:1 + FS/SaaS clusters</div>
      </div>

      {/* Outcomes */}
      <div className="stat">
        <div className="stat__label">Outcomes</div>
        <div className="stat__num">
          <div className="stat__value">
            ${data.outcomes.value}
            <span className="stat__unit">{data.outcomes.unit}</span>
          </div>
        </div>
        <div className="stat__hint">{data.outcomes.hint}</div>
      </div>
    </div>
  );
}

/* =========================
   Tiny shared UI bits
========================= */
function Hero({ title, sub }) {
  return (
    <div className="hero">
      <div className="hero__bg">
        <img className="hero__wm" src={C_RGB} alt="" aria-hidden />
      </div>
      <h2 className="hero__title">{title}</h2>
      {sub ? <p className="hero__sub">{sub}</p> : null}
    </div>
  );
}
function Note({ children }) {
  return <div className="note">{children}</div>;
}

/* ============ main ============ */
export default function ABMGuide() {
  useAutoTitle("ABM Marketing & Sales 101");

  const pages = [
    <Page1_Cover key="p1" />,
    <Page2_Focus key="p2" />,
    <Page3_FromICP key="p3" />,
    <Page4_MetricsStack key="p4" />,
    <Page5_Scorecard key="p5" />,
    <Page6_StageJourney key="p6" />,
    <Page7_RolesHandoffs key="p7" />,
    <Page8_BuyingCommittee key="p8" />,
    <Page9_DashboardGlossary key="p9" />,
    // unchanged interactive pages are defined later:
    <Page10_Roundtable key="p10" />,
    <Page11_Syndication key="p11" />,
    <Page12_Wheel key="p12" />,
  ];

  return (
    <div className="pdf-wrap">
      <style>{CSS}</style>

      <header className="pdf-top">
        <a className="brand" href="#marketing" aria-label="Back to hub">
          <picture>
            <source srcSet={LOGO_SVG} type="image/svg+xml" />
            <img
              className="brand__img"
              src={LOGO_PNG}
              alt="Coursera"
              height="18"
            />
          </picture>
          <span className="forbiz">for Business</span>
        </a>
        <div className="top-actions">
          <a className="btn btn--ghost" href="#marketing">
            Back to hub
          </a>
          <button className="btn btn--primary" onClick={() => window.print()}>
            Download PDF
          </button>
        </div>
      </header>

      <main className="sheet">
        {pages.map((node, i) => (
          <section className="page" key={i}>
            {node}
            <footer className="page__foot" aria-hidden>
              {i + 1}
            </footer>
          </section>
        ))}
      </main>
    </div>
  );
}

/* ==================== PAGES (1–9 updated) ==================== */

function Page1_Cover() {
  return (
    <div className="intro">
      <div className="intro__hero">
        {/* LEFT: Title + lede */}
        <div className="intro__copy">
          <h1 className="intro__title">ABM Marketing &amp; Sales 101</h1>
          <p className="intro__subtitle">What to focus on and why it matters</p>

          <div className="lede">
            This guide helps Coursera for Business marketing and sales teams
            focus their ABM energy where it matters most. It walks through how
            we run and read <b>ABM 1:1</b> and <b>1:Few</b>
            programs in <b>Financial Services</b> and <b>SaaS/Tech</b>, and how
            we use a small set of shared metrics so everyone can see, at a
            glance, how our account-based work is contributing to real pipeline.
            We’ll zoom in on three things:
            <ul className="tight" style={{ marginTop: 8 }}>
              <li>How Coursera defines ABM 1:1 and ABM 1:Few</li>
              <li>The small set of metrics that matter most</li>
              <li>
                Practical examples of how to read programs through an ABM lens,
                especially in our priority 1:Few industries:{" "}
                <b>Financial Services</b> and <b>SaaS/Tech</b>
              </li>
            </ul>
          </div>
        </div>

        {/* RIGHT: Thumbnail card */}
        <figure className="intro__art" aria-label="ABM overview wheel">
          <img src={COVER_THUMB} alt="ABM Overview Guide wheel" />
        </figure>

        {/* subtle shapes (optional) */}
        <svg className="c c--left" viewBox="0 0 200 200" aria-hidden>
          <circle cx="100" cy="100" r="100" fill="#ffffff" />
        </svg>
        <svg className="c c--ring" viewBox="0 0 200 200" aria-hidden>
          <circle
            cx="100"
            cy="100"
            r="100"
            fill="none"
            stroke="#ffffff"
            strokeWidth="22"
          />
        </svg>
      </div>
    </div>
  );
}

function Page2_Focus() {
  return (
    <>
      <Hero
        title="Making ABM Work"
        sub="Simple, aligned measurement for Coursera’s Marketing team"
      />
      <div className="grid">
        <section className="panel">
          <h3>Why ABM at Coursera for Business</h3>
          <p className="muted">
            Our buyers are committees across L&amp;D/Talent, Tech/Data,
            HR/People, and functional leaders— high value, long cycles, many
            voices. We can’t market to everyone, so we focus where Coursera can
            make the biggest impact:
          </p>
          <ul className="tight">
            <li>
              ABM <b>1:1</b> strategic accounts
            </li>
            <li>
              ABM <b>1:Few</b> clusters in <b>Financial Services</b> and{" "}
              <b>SaaS/Tech</b>
            </li>
          </ul>
        </section>

        <section className="panel">
          <h3>What you’ll get from this guide</h3>
          <ul className="tight">
            <li>
              A clear definition of ABM <b>1:1</b> and <b>1:Few</b> at Coursera
            </li>
            <li>The small set of ABM metrics that actually matter</li>
            <li>
              How to read programs (by stage) in <b>FS</b> and <b>SaaS/Tech</b>
            </li>
            <li>Examples that keep reporting simple and decision-oriented</li>
          </ul>
        </section>
      </div>
    </>
  );
}

function Page3_FromICP() {
  return (
    <>
      <Hero title="From ICP to Account-Based Programs" />

      <div className="grid grid--split">
        {/* LEFT: ICP/TAL */}
        <section className="panel">
          <p className="kicker">ICP → TAL</p>
          <h3 style={{ marginTop: 0 }}>Three layers of focus</h3>

          <ol className="flow">
            <li>
              <b>ICP / TAM</b> — All organizations that look like great Coursera
              for Business customers.
            </li>
            <li>
              <b>Target Account List (TAL)</b> — The portion of the ICP we
              deliberately pursue this year.
            </li>
          </ol>

          <div className="note note--soft">
            ICP/TAL define the universe and the annual focus. ABM is how we
            treat a subset of those accounts differently.
          </div>
        </section>

        {/* RIGHT: ABM highlight column */}
        <section className="panel panel--abmRight">
          <span className="badge-pill">ABM programs</span>
          <h3 style={{ marginTop: 10 }}>What’s different in ABM?</h3>

          <ul className="tight">
            <li>
              <b>ABM 1:1 accounts</b>
              <ul className="tight">
                <li>Strategic, complex, or high-visibility accounts</li>
                <li>Deep Sales partnership and tailored programs</li>
                <li>Clear ownership across Marketing and Sales</li>
              </ul>
            </li>

            <li style={{ marginTop: 8 }}>
              <b>ABM 1:Few clusters</b>
              <ul className="tight">
                <li>Look-alike sets by industry/problem pattern</li>
                <li>One-to-few plays, messaging, and offers</li>
                <li>
                  Priority industries now: <b>Financial Services</b> and{" "}
                  <b>SaaS/Tech</b>
                </li>
              </ul>
            </li>
          </ul>

          <div className="callout">
            <b>Planning question.</b> Which accounts belong in ABM <b>1:1</b>,
            which belong in our <b>FS</b> or <b>SaaS/Tech</b> 1:Few programs,
            and which stay in broader programs?
          </div>

          <div className="callout callout--muted">
            ABM is one team working one plan—proving impact at the{" "}
            <b>account</b> level, not just the lead level.
          </div>
        </section>
      </div>
    </>
  );
}

function Page4_MetricsStack() {
  return (
    <>
      <Hero title="A Simple ABM Metrics Stack" />
      <div className="grid">
        <section className="panel">
          <h3>Coverage — are we even in the game?</h3>
          <ul className="tight">
            <li>
              Do we have <b>L&amp;D</b>, <b>Tech/Data</b>, <b>HR/People</b>, and
              a <b>business sponsor</b>?
            </li>
            <li>
              % ABM <b>1:1</b> with all core personas mapped
            </li>
            <li>
              % <b>FS</b> / <b>SaaS/Tech</b> 1:Few with ≥2 personas
            </li>
            <li>Data freshness (contacts verified/touched ≤90 days)</li>
          </ul>
        </section>
        <section className="panel">
          <h3>Engagement — are the right people leaning in?</h3>
          <ul className="tight">
            <li>
              % ABM <b>1:1</b> with meaningful engagement in last 30–60 days
            </li>
            <li>
              # engaged accounts in <b>FS</b> / <b>SaaS/Tech</b> clusters
            </li>
            <li>
              Signals from senior decision-makers (events, key content, pricing)
            </li>
          </ul>
        </section>
        <section className="panel">
          <h3>Impact — is ABM changing the business?</h3>
          <ul className="tight">
            <li>
              Opportunities created in ABM <b>1:1</b>
            </li>
            <li>
              Opps sourced/influenced in <b>FS</b> / <b>SaaS/Tech</b> 1:Few
            </li>
            <li>
              Pipeline value; early read on win-rate/deal size/sales-cycle vs.
              non-ABM
            </li>
          </ul>
          <Note>
            Keep it explainable on one page. Consistency beats complexity.
          </Note>
        </section>
      </div>
    </>
  );
}

function Page5_Scorecard() {
  return (
    <>
      <Hero title="ABM Scorecard — 5 Numbers That Matter" />
      <StatStrip />

      <div className="grid">
        <section className="panel">
          <h3>1) Coverage — ABM Account Coverage</h3>
          <p className="muted">
            <b>What:</b> % of ABM accounts (1:1 + 1:Few) with ≥2 core personas
            mapped.
          </p>
          <p className="muted">
            <b>Why:</b> If we don’t know the buying committee, ABM is guessing.
          </p>

          <h3 style={{ marginTop: 10 }}>2) Activity — Active ABM Accounts</h3>
          <p className="muted">
            <b>What:</b> #/% of ABM accounts with meaningful engagement in last
            30–60 days.
          </p>
          <p className="muted">
            <b>Why:</b> Shows whether ABM is waking up the right FS/SaaS
            accounts, not just traffic.
          </p>

          <h3 style={{ marginTop: 10 }}>3) Depth — Multi-Threaded Accounts</h3>
          <p className="muted">
            <b>What:</b> # of ABM accounts with 2+ personas engaged.
          </p>
          <p className="muted">
            <b>Why:</b> Single-threaded deals stall; this tracks real buying
            committees.
          </p>
        </section>

        <section className="panel">
          <h3>4) Pipeline — ABM Pipeline Value</h3>
          <p className="muted">
            <b>What:</b> Total qualified pipeline from ABM 1:1 + FS/SaaS 1:Few.
          </p>
          <p className="muted">
            <b>Why:</b> Connects ABM activity to opportunity creation.
          </p>

          <h3 style={{ marginTop: 10 }}>5) Outcomes — ABM Wins</h3>
          <p className="muted">
            <b>What:</b> Closed-won from ABM accounts and simple win-rate
            comparison over time.
          </p>
          <p className="muted">
            <b>Why:</b> The “so what”: focused ABM should outperform broad
            demand gen.
          </p>
        </section>
      </div>
    </>
  );
}

function Page6_StageJourney() {
  return (
    <>
      <Hero title="ABM Journey by Stage — Spot the Signal, Make One Move" />
      <div className="grid">
        <section className="panel">
          <h3>Awareness</h3>
          <ul className="tight">
            <li>
              <b>Signal:</b> No real activity from the account
            </li>
            <li>
              <b>Move:</b> Put <b>FS</b> / <b>SaaS-Tech</b> stories in front of
              them
            </li>
          </ul>

          <h3 style={{ marginTop: 10 }}>Engagement</h3>
          <ul className="tight">
            <li>
              <b>Signal:</b> Repeat visits, more than one contact
            </li>
            <li>
              <b>Move:</b> Plan 1–2 role-based follow-ups
            </li>
          </ul>

          <h3 style={{ marginTop: 10 }}>Consideration</h3>
          <ul className="tight">
            <li>
              <b>Signal:</b> Deep content views, “how would this work?”
              questions
            </li>
            <li>
              <b>Move:</b> Share one clear use case and value story
            </li>
          </ul>
        </section>

        <section className="panel">
          <h3>Meeting</h3>
          <ul className="tight">
            <li>
              <b>Signal:</b> Good meeting, fuzzy or slipping next step
            </li>
            <li>
              <b>Move:</b> Send recap and propose one concrete next step
            </li>
          </ul>

          <h3 style={{ marginTop: 10 }}>Pipeline / Evaluation</h3>
          <ul className="tight">
            <li>
              <b>Signal:</b> Opportunity created, stage not moving
            </li>
            <li>
              <b>Move:</b> Align with AE on a simple “win plan”
            </li>
          </ul>

          <Note>
            For every ABM <b>1:1</b> or <b>1:Few</b> account: know the stage,
            name the signal, choose one next move.
          </Note>
        </section>
      </div>
    </>
  );
}

function Page7_RolesHandoffs() {
  return (
    <>
      <Hero title="TAL → Meeting Flow (Roles & Handoffs)" />
      <div className="grid">
        <section className="panel">
          <h3>Marketing</h3>
          <ul className="tight">
            <li>Define ABM 1:1 and FS/SaaS 1:Few programs</li>
            <li>Build persona maps &amp; key hypotheses</li>
            <li>Activate 1:1 and 1:Few plays (events, content, workshops)</li>
            <li>Surface account/cluster signals consistently</li>
          </ul>
          <h3 className="h4" style={{ marginTop: 10 }}>
            SDRs
          </h3>
          <ul className="tight">
            <li>Monitor ABM signals; prioritize by persona</li>
            <li>Connect signals → conversations/meetings</li>
            <li>Log activities &amp; learnings in shared notes</li>
          </ul>
        </section>
        <section className="panel">
          <h3>Sales</h3>
          <ul className="tight">
            <li>Own deal strategy for ABM 1:1</li>
            <li>Coordinate discovery depth &amp; executive alignment</li>
            <li>Use ABM programs as fuel for multi-threading</li>
          </ul>
          <h3 className="h4" style={{ marginTop: 10 }}>
            Shared
          </h3>
          <ul className="tight">
            <li>Align on stage definitions &amp; exit criteria</li>
            <li>Review ABM 1:1 and 1:Few performance regularly</li>
            <li>Decide together which accounts move into more intensive ABM</li>
          </ul>
          <Note>
            Simple flow = fewer drops. If a step isn’t visible in CRM or
            dashboards, it might not be happening.
          </Note>
        </section>
      </div>
    </>
  );
}

function Page8_BuyingCommittee() {
  return (
    <>
      <Hero title="Buying Committee & ABM Support You Can Lean On" />
      <div className="grid">
        <section className="panel">
          <h3>Who we care about</h3>
          <dl className="gloss">
            {[
              [
                "L&D / Talent Leader",
                "Impact, adoption, verified skills, culture of learning",
              ],
              [
                "Tech / Data Leader",
                "Platform fit, security, integrations, AI capabilities",
              ],
              [
                "HR / People Leader",
                "Retention, engagement, equity, employer brand",
              ],
              [
                "Business Sponsor",
                "Team performance, productivity, competitive advantage",
              ],
            ].map(([k, v]) => (
              <div className="gloss__row" key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
          <Note>
            Choose first contact by fit and influence; tune language to the
            persona.
          </Note>
        </section>
        <section className="panel">
          <h3>What ABM brings to your outreach</h3>
          <ul className="tight">
            <li>
              <b>Persona one-pagers</b> with priorities, objections, proof
              points
            </li>
            <li>
              <b>Stage-mapped assets</b> and <b>role-based snippets</b> ready to
              drop in
            </li>
            <li>
              <b>Signal briefs</b>: short lists of accounts with stage, recent
              signal, suggested next move
            </li>
            <li>
              <b>Deal kits</b>: use-case stories and references for <b>FS</b>{" "}
              and <b>SaaS/Tech</b>
            </li>
            <li>
              <b>Executive air cover</b>: invites, roundtables,
              thought-leadership angles
            </li>
            <li>
              <b>Ops help</b>: simple logging so everyone sees one story
            </li>
          </ul>
          <Note>
            We’re here to make your conversations easier and sharper—tell us
            what’s landing and we’ll tune the assets.
          </Note>
        </section>
      </div>
    </>
  );
}

function Page9_DashboardGlossary() {
  return (
    <>
      <Hero title="Dashboard Glossary & Bite-Sized Examples" />
      <div className="grid">
        <section className="panel">
          <h3>Fast definitions</h3>
          <dl className="gloss">
            {[
              [
                "TAL",
                "Target Account List agreed by Sales, SDR, and Marketing",
              ],
              [
                "ABM 1:1",
                "Named strategic accounts receiving one-to-one programs",
              ],
              [
                "ABM 1:Few",
                "Small sets of lookalike accounts (currently FS and SaaS/Tech) receiving one-to-few plays",
              ],
              [
                "MQA",
                "Marketing Qualified Account: account-level threshold of intent/engagement",
              ],
              ["Stage Velocity", "Avg. days ABM accounts sit in each stage"],
              [
                "Influenced Pipeline",
                "Pipeline with verified Marketing/ABM touches",
              ],
              [
                "Coverage",
                "Presence of key personas in an ABM account/cluster",
              ],
              [
                "Engaged Account",
                "ABM account with recent meaningful activity (events, key assets, meetings)",
              ],
            ].map(([k, v]) => (
              <div className="gloss__row" key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </section>
        <section className="panel">
          <h3>Bite-sized examples</h3>
          <ul className="tight">
            <li>
              <b>Engaged ABM account, no meeting yet</b> — 2–3 quality touches
              in 7–10 days; <i>Action:</i> change the offer/persona; shift to
              FS/SaaS asset; SDR follow-up with a pointed question.
            </li>
            <li>
              <b>Stage-velocity spike in Consideration</b> — accounts lingering;{" "}
              <i>Action:</i> run a deal clinic or industry workshop + one strong
              proof-point mapped to L&amp;D and Tech/Data.
            </li>
            <li>
              <b>When to multi-thread</b> — after a positive meeting in ABM 1:1;{" "}
              <i>Action:</i> role-specific nudge (FS/SaaS case, AI-skills
              success) vs. generic creds deck.
            </li>
          </ul>
          <Note>
            Use these as micro-tooltips inside real dashboards. The goal is
            turning numbers into next moves.
          </Note>
        </section>
      </div>
    </>
  );
}

/* ==================== Interactive pages (unchanged) ==================== */

function Page10_Roundtable() {
  return (
    <section>
      <Hero title="Roundtable Snapshot" />
      <div className="panel">
        <RoundtableDashboard />
      </div>
    </section>
  );
}

function Page11_Syndication() {
  return (
    <section>
      <Hero title="Content Syndication" />
      <div className="panel">
        <ContentSyndicationDashboard />
      </div>
    </section>
  );
}

function Page12_Wheel() {
  return (
    <section>
      <Hero title="Conduit Wheel Builder" />
      <div className="panel">
        <WheelBuilderCoursera />
      </div>
    </section>
  );
}
